import {
  Box,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  Typography,
  Button,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import PaidIcon from '@mui/icons-material/Paid';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExploreIcon from '@mui/icons-material/Explore';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SimpleMonthlyRentChart from '@/features/dashboard/components/SimpleMonthlyRentChart.tsx';
import InvestmentStatusBarChart from '@/features/dashboard/components/InvestmentStatusBarChart.tsx';
import InvestmentTypePieChart from '@/features/dashboard/components/InvestmentTypePieChart.tsx';
import CashflowChart from '@/features/dashboard/components/CashflowChart.tsx';
import { DashboardHeader } from './components/DashboardHeader';
import { StatsGrid, StatItem } from './components/StatsGrid';
import { ChartContainer } from './components/ChartContainer';
import { useDashboard } from '@/lib/api/dashboard';
import { DashboardPeriodPreset, DashboardStats } from '@inversor-hub/types';
import { formatCurrency, formatPercentage } from '@/utils/misc.ts';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '@/hooks/useAnalytics.ts';
import {
  ANALYTICS_PLATFORM,
  trackProductEventOncePerSession,
} from '@/lib/analytics/product-events';
import QuickGuideDashboardSection from '@/features/quick-guide/QuickGuideDashboardSection';

function createStatsFromApiData(stats: DashboardStats): StatItem[] {
  return [
    {
      title: 'Total Inversiones',
      value: stats.totalInvestments,
      icon: <BusinessCenterIcon fontSize="small" />,
    },
    {
      title: 'Capital Invertido',
      value: formatCurrency(stats.totalCapitalInvested),
      icon: <PaidIcon fontSize="small" />,
    },
    {
      title: 'Renta mensual actual',
      value: formatCurrency(stats.lastMonthRent),
      icon: <HomeWorkIcon fontSize="small" />,
    },
    {
      title: 'Rentabilidad Neta Media',
      value: formatPercentage(stats.averageReturn),
      icon: <TrendingUpIcon fontSize="small" />,
      tooltip: 'No incluye impuestos ni intereses',
    },
  ];
}

function formatMonth(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function getPeriod(preset: DashboardPeriodPreset) {
  const now = new Date();
  const to = new Date(now.getFullYear(), now.getMonth(), 1);
  const from =
    preset === 'YTD'
      ? new Date(now.getFullYear(), 0, 1)
      : new Date(
          now.getFullYear(),
          now.getMonth() - ({ '6M': 5, '12M': 11, '24M': 23 }[preset] ?? 11),
          1
        );
  const formatter = new Intl.DateTimeFormat('es-ES', {
    month: 'short',
    year: 'numeric',
  });

  return {
    query: {
      from: formatMonth(from),
      to: formatMonth(to),
    },
    label: `${formatter.format(from)} – ${formatter.format(to)}`,
  };
}

export function InvestorDashboard() {
  const theme = useTheme();
  const [periodPreset, setPeriodPreset] = useState<DashboardPeriodPreset>('YTD');
  const period = useMemo(() => getPeriod(periodPreset), [periodPreset]);
  const { data: dashboardData, isLoading, isFetching, error } = useDashboard(period.query);
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (!dashboardData) {
      return;
    }

    trackProductEventOncePerSession(trackEvent, 'dashboard_viewed', 'dashboard_viewed', {
      role: 'investor',
      platform: ANALYTICS_PLATFORM,
    });
  }, [dashboardData, trackEvent]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 170px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <DashboardHeader
          title="Dashboard"
          period={periodPreset}
          periodLabel={period.label}
          onPeriodChange={setPeriodPreset}
        />
        <Alert severity="error" sx={{ mt: 2 }}>
          Error al cargar los datos del dashboard.
        </Alert>
      </Box>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const hasInvestments = dashboardData.stats.totalInvestments > 0;
  const stats = createStatsFromApiData(dashboardData.stats);

  const handleStartInvesting = () => {
    navigate('/oportunidades');
  };

  const handleViewInvestments = () => {
    navigate('/inversiones');
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <DashboardHeader
        title="Dashboard"
        period={periodPreset}
        periodLabel={period.label}
        onPeriodChange={setPeriodPreset}
        isPeriodLoading={isFetching}
        action={
          <>
            <Button
              variant="outlined"
              startIcon={<AssessmentOutlinedIcon />}
              onClick={() => navigate('/dashboard/informe')}
              disabled={!hasInvestments}
            >
              Informe financiero
            </Button>
            <Button
              variant="outlined"
              startIcon={<ReceiptLongOutlinedIcon />}
              onClick={() => navigate('/dashboard/fiscal')}
              disabled={!hasInvestments}
            >
              Resumen fiscal
            </Button>
          </>
        }
      />

      <QuickGuideDashboardSection />

      <StatsGrid stats={stats} />

      {/* Welcome message for new users */}
      {!hasInvestments && (
        <Box sx={{ mb: 4, position: 'relative', zIndex: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              textAlign: 'center',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}F0 0%, ${theme.palette.primary.dark}F0 100%)`,
              color: 'white',
              borderRadius: 2,
            }}
          >
            <ExploreIcon sx={{ fontSize: '2.5rem', mb: 1 }} />
            <Typography variant="h5" gutterBottom fontWeight={600}>
              ¡Bienvenido a tu Dashboard de Inversiones!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
              Así se verán tus métricas y gráficos una vez que realices tus primeras inversiones.
              Explora oportunidades para comenzar a invertir.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleStartInvesting}
              sx={{
                backgroundColor: 'white',
                color: 'primary.main',
                fontWeight: 600,
                textTransform: 'none',
                px: 4,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                },
              }}
            >
              Explorar Oportunidades
            </Button>
          </Paper>
        </Box>
      )}

      {/* Charts Grid */}
      <Grid container spacing={{ xs: 2.5, md: 4 }}>
        {/* Monthly Rent Chart */}
        <Grid item xs={12} lg={8}>
          <ChartContainer
            title="Estimación mensual del alquiler"
            subtitle="El neto operativo es el alquiler menos los gastos recurrentes del inmueble. El cashflow descuenta además la cuota hipotecaria."
          >
            <SimpleMonthlyRentChart data={dashboardData.monthlyRentData} height={400} />
          </ChartContainer>
        </Grid>

        {/* Investment Status Bar Chart */}
        <Grid item xs={12} lg={4}>
          <ChartContainer title="Estado de Inversiones" subtitle="Distribución por estado actual">
            <InvestmentStatusBarChart
              data={dashboardData.investmentStatusData}
              height={400}
              onStartInvesting={handleViewInvestments}
            />
          </ChartContainer>
        </Grid>

        {/* Monthly Cash Flow */}
        <Grid item xs={12} lg={8}>
          <ChartContainer
            title="Flujo de Caja Mensual"
            subtitle="Ingresos y gastos registrados por mes. El mes actual puede estar incompleto."
          >
            <CashflowChart data={dashboardData.monthlyCashflowData} height={400} />
          </ChartContainer>
        </Grid>

        {/* Investment Type Pie Chart */}
        <Grid item xs={12} lg={4}>
          <ChartContainer title="Tipos de Inversión" subtitle="Distribución por tipo de inversión">
            <InvestmentTypePieChart
              data={dashboardData.investmentTypeData}
              height={400}
              onStartInvesting={handleStartInvesting}
            />
          </ChartContainer>
        </Grid>
      </Grid>
    </Box>
  );
}
